import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { X, Trash2, Calculator, ChevronRight, AlertTriangle } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

function ParlayCart() {
  const { selections, removeSelection, clearCart, selectionCount } = useCart();
  const { session } = useAuth();
  const [parlayData, setParlayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const isMobile = () => window.innerWidth <= 1200;
  const [isCollapsed, setIsCollapsed] = useState(() => isMobile());
  const [trackingParlay, setTrackingParlay] = useState(false);
  const [trackAmount, setTrackAmount] = useState('');
  const [parlayTracked, setParlayTracked] = useState(false);

  // Reset tracked state when selections change
  useEffect(() => {
    setParlayTracked(false);
  }, [selections]);

  // Calculate parlay when selections change
  useEffect(() => {
    if (selections.length < 2) {
      setParlayData(null);
      return;
    }

    const calculateParlay = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/parlay/calculate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            selections: selections.map(s => ({
              eventId: s.eventId,
              team: s.team,
              odds_by_book: s.oddsAllBooks,
              selected_odds: s.odds,
            }))
          })
        });
        const data = await response.json();
        if (data.success) {
          setParlayData(data);
        }
      } catch (err) {
        console.error('Parlay calculation error:', err);
      } finally {
        setLoading(false);
      }
    };

    calculateParlay();
  }, [selections]);

  const formatOdds = (odds) => odds > 0 ? `+${odds}` : odds;

  // Check for stale odds by comparing selection odds to current localStorage odds
  const getStaleSelections = () => {
    try {
      const currentOdds = JSON.parse(localStorage.getItem('prevOdds') || '{}');
      return selections.filter(sel => {
        const key = `${sel.eventId}_${sel.sportsbookKey}`;
        const side = sel.team === 'Draw' ? 'draw' : null;
        if (side === 'draw') {
          const storedOdds = currentOdds[`${key}_draw`];
          return storedOdds != null && storedOdds !== sel.odds;
        }
        const homeOdds = currentOdds[`${key}_home`];
        const awayOdds = currentOdds[`${key}_away`];
        if (homeOdds === sel.odds || awayOdds === sel.odds) return false;
        return homeOdds != null || awayOdds != null;
      });
    } catch { return []; }
  };

  const handleTrackParlay = async () => {
    if (!session || !parlayData) return;
    const legCount = selections.length;
    const legs = selections.map(sel => ({
      event_id: sel.eventId,
      sport_key: sel.sportKey || null,
      home_team: sel.homeTeam || null,
      away_team: sel.awayTeam || null,
      team: sel.team,
      side: sel.team,
      market: sel.market || 'h2h',
      point: sel.point ?? null,
      commence_time: sel.commenceTime || null,
    }));
    try {
      const resp = await fetch(`${API_BASE_URL}/user/bets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          betType: 'parlay',
          sportsbook: parlayData.best_odds?.display_name || parlayData.best_book || '',
          eventName: `${legCount}-leg parlay`,
          market: 'parlay',
          side: selections.map(s => s.team).join(' / '),
          odds: parlayData.best_odds?.american_odds || 0,
          amount: parseFloat(trackAmount) || 0,
          notes: JSON.stringify({ legs }),
        }),
      });
      if (resp.ok) {
        setParlayTracked(true);
        setTrackingParlay(false);
        setTrackAmount('');
      }
    } catch (err) {
      console.error('Track parlay failed:', err);
    }
  };

  const staleSelections = getStaleSelections();
  const hasStaleOdds = staleSelections.length > 0;

  // Detect same-game legs
  const eventIdCounts = selections.reduce((acc, sel) => {
    acc[sel.eventId] = (acc[sel.eventId] || 0) + 1;
    return acc;
  }, {});
  const isSGP = selections.length >= 2 && Object.keys(eventIdCounts).length === 1;
  const hasMixedSameGame = !isSGP && Object.values(eventIdCounts).some(c => c > 1);
  const hasSameGameLegs = isSGP || hasMixedSameGame;

  return (
    <div className={`parlay-cart ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="cart-header" onClick={() => setIsCollapsed(!isCollapsed)}>
        <h3>
          <span className="cart-icon-wrap">
            <Calculator size={20} />
            {isCollapsed && selectionCount > 0 && (
              <span className="cart-count-badge">{selectionCount}</span>
            )}
          </span>
          <span>Parlay Cart ({selectionCount})</span>
        </h3>
        {!isCollapsed && (
          <button className="collapse-btn">
            <ChevronRight size={18} />
          </button>
        )}
      </div>

      {!isCollapsed && (
        <div className="cart-content">
          {selections.length === 0 ? (
            <p className="empty-cart">Click on odds to add selections to your parlay</p>
          ) : (
            <>
              {hasStaleOdds && (
                <div className="stale-odds-warning">
                  <AlertTriangle size={16} />
                  <span>Some odds may have changed since added to cart</span>
                </div>
              )}
              <div className="selections-list">
                {selections.map(sel => (
                  <div key={sel.id} className="selection-item">
                    <div className="selection-info">
                      <span className="team">
                        {sel.team}
                        {sel.market === 'spreads' && sel.point != null && ` (${sel.point > 0 ? '+' : ''}${sel.point})`}
                        {sel.market === 'totals' && sel.point != null && ` (${sel.point})`}
                      </span>
                      <span className="odds">{formatOdds(sel.odds)}</span>
                      <span className="event">
                        {sel.eventName}
                        {sel.market && sel.market !== 'h2h' && (
                          <span className="market-tag">
                            {sel.market === 'spreads' ? 'Spread' : sel.market === 'totals' ? 'Total' : 'Prop'}
                          </span>
                        )}
                      </span>
                    </div>
                    <button
                      className="remove-btn"
                      onClick={() => removeSelection(sel.id)}
                      title="Remove from parlay"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>

              {selections.length >= 2 && (
                <div className="parlay-results">
                  <h4>{isSGP ? 'Best SGP Odds (Est.)' : hasMixedSameGame ? 'Best Parlay Odds (Est.)' : 'Best Parlay Odds'}</h4>
                  {loading ? (
                    <div className="loading-parlay">Calculating...</div>
                  ) : parlayData ? (
                    <>
                      <div className="best-parlay">
                        <span className="book">{parlayData.best_odds.display_name}</span>
                        <span className="parlay-odds">
                          {formatOdds(parlayData.best_odds.american_odds)}
                        </span>
                      </div>

                      <details className="all-books-parlay">
                        <summary>All Sportsbooks ({Object.keys(parlayData.parlay_odds).length})</summary>
                        <div className="parlay-books-list">
                          {Object.entries(parlayData.parlay_odds)
                            .sort((a, b) => b[1].decimal_odds - a[1].decimal_odds)
                            .map(([key, data]) => (
                              <div
                                key={key}
                                className={`parlay-book ${key === parlayData.best_book ? 'is-best' : ''}`}
                              >
                                <span>{data.display_name}</span>
                                <span>{formatOdds(data.american_odds)}</span>
                              </div>
                            ))}
                        </div>
                      </details>

                      {hasSameGameLegs && (
                        <div className="sgp-disclaimer">
                          <AlertTriangle size={13} />
                          {isSGP
                            ? 'All legs are from the same game. Actual SGP odds at each book will be lower due to correlated outcomes — use these estimates to identify which book to check first, then build the SGP in their app.'
                            : 'This parlay includes multiple legs from the same game. Odds are estimates — same-game legs are correlated, so actual odds will be lower than shown.'}
                        </div>
                      )}

                      {/* Track Parlay */}
                      {session && (
                        <div className="parlay-track-section">
                          {trackingParlay ? (
                            <div className="parlay-track-form">
                              <input
                                type="number"
                                className="parlay-track-input"
                                placeholder="$ amount"
                                value={trackAmount}
                                onChange={e => setTrackAmount(e.target.value)}
                                autoFocus
                              />
                              <button className="parlay-track-save" onClick={handleTrackParlay}>Track</button>
                              <button className="parlay-track-cancel" onClick={() => { setTrackingParlay(false); setTrackAmount(''); }}>✕</button>
                              {(() => {
                                const amt = parseFloat(trackAmount);
                                const o = parlayData?.best_odds?.american_odds;
                                if (!amt || amt <= 0 || !o) return null;
                                const profit = amt * (o > 0 ? o / 100 : 100 / Math.abs(o));
                                return <span className="ctf-to-win">To win: +${profit.toFixed(2)}</span>;
                              })()}
                            </div>
                          ) : (
                            <button
                              className={`parlay-track-btn${parlayTracked ? ' tracked' : ''}`}
                              onClick={() => !parlayTracked && setTrackingParlay(true)}
                            >
                              {parlayTracked ? '✓ Parlay Tracked' : 'Track Parlay'}
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="parlay-error">Unable to calculate parlay</div>
                  )}
                </div>
              )}

              {selections.length === 1 && (
                <p className="parlay-hint">Add at least one more selection for parlay odds</p>
              )}

              <button className="clear-cart-btn" onClick={clearCart}>
                <Trash2 size={16} />
                Clear All
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ParlayCart;
